import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { StorageManager } from '../storage/index.js';
import { throwIfAccessLocked } from './access.js';

export interface NormalizedWord {
  input: string;
  /** Dictionary form ("neuen" -> "neu", "steht" -> "stehen"). */
  lemma: string;
  part_of_speech: string;
  is_german_word: boolean;
  is_proper_noun: boolean;
}

/** Lower-cased input -> normalization, or null when normalization is unavailable. */
export type Normalizer = (
  words: string[],
  language: string
) => Promise<Map<string, NormalizedWord> | null>;

/** The server accepts this many words per request. */
const CHUNK = 200;

/**
 * Dictionary forms for the words of an import, from the `normalize-words` Edge Function (one AI
 * call per 200 words). Returns null when it isn't available (signed out, offline, local dev mode,
 * daily limit, server error) so the importer can fall back to its offline lemmatizer. Throws
 * AccessLockedError when the learner has no access (services/access.ts).
 */
export const normalizeWords: Normalizer = async (words, language) => {
  const unique = Array.from(new Set(words.map((w) => w.trim()).filter(Boolean)));
  if (unique.length === 0) return new Map();

  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) return null;
  const supabase = getSupabase(profile.supabase_url, key);
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return null;

  const result = new Map<string, NormalizedWord>();
  for (let start = 0; start < unique.length; start += CHUNK) {
    const chunk = unique.slice(start, start + CHUNK);
    const { data, error } = await supabase.functions.invoke<{ results?: NormalizedWord[] }>(
      'normalize-words',
      { body: { words: chunk, language } }
    );
    if (error) await throwIfAccessLocked(error);
    if (error || !Array.isArray(data?.results)) {
      console.warn('[LingoPing] Word normalization unavailable:', error?.message);
      return null;
    }
    for (const r of data.results) {
      if (r && typeof r.input === 'string' && typeof r.lemma === 'string' && r.lemma.trim()) {
        result.set(r.input.trim().toLowerCase(), { ...r, lemma: r.lemma.trim() });
      }
    }
  }
  return result;
};
